/**
 * The pieces a generated report's document is made of: the paper it sits on,
 * the two-column page layout, the section heading, the paragraph and the
 * machine link. Shared by the weekly status, the risk email and the deck.
 *
 * `report-doc`, `rh` and `note` are stable class names the download's
 * stylesheet keys off (`report-export.ts`). UI layer.
 */

import type { ReactNode, RefObject } from 'react';
import { Link } from 'react-router-dom';

/** The two-column report layout — document left, panel right; stacked below `@5xl`. */
export function ReportLayout({ doc, side }: { doc: ReactNode; side: ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-start gap-4 @5xl:grid-cols-[minmax(0,1fr)_280px]">
      <div className="min-w-0">{doc}</div>
      <div className="min-w-0">{side}</div>
    </div>
  );
}

/** The paper the document sits on. `report-doc` is the download's root class. */
export function ReportPaper({
  paperRef,
  children,
  label,
  'data-comment-anchor': commentAnchor,
}: {
  paperRef: RefObject<HTMLDivElement>;
  children: ReactNode;
  label: string;
  /** Set by `<Commentable>` (cloneElement); forwarded to the root element so the anchor reaches the DOM. */
  'data-comment-anchor'?: string;
}) {
  return (
    <div
      ref={paperRef}
      data-comment-anchor={commentAnchor}
      className="report-doc min-w-0 rounded-2xl border px-5 py-5 @3xl:px-6"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-1)' }}
      role="document"
      aria-label={label}
    >
      {children}
    </div>
  );
}

/** A section heading inside a report document — the demo's `h3.rh`. */
export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="rh mb-1 mt-4 text-[13px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
      {children}
    </h3>
  );
}

/** A paragraph inside a report document; `note` is the demo's muted `.note`. */
export function Para({ children, note }: { children: ReactNode; note?: boolean }) {
  return (
    <p
      className={`${note ? 'note text-[11.5px]' : 'text-[12.5px]'} my-1.5`}
      style={{ color: note ? 'var(--ink-tertiary)' : 'var(--ink-secondary)' }}
    >
      {children}
    </p>
  );
}

/** A machine id that opens the machine page — the demo's `show('machine', id)`. */
export function MachineLink({ id }: { id: string }) {
  return (
    <Link
      to={`/machines/${id}`}
      className="mono underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
      style={{ color: 'var(--accent)' }}
    >
      {id}
    </Link>
  );
}
